import { useDynamicForm } from "@/components/dynamic-form/hook/useDynamicForm";
import { enhanceEndTime } from "@/components/dynamic-form/formatter/enhanceEndTime";
import { useWatchPagination } from "@/components/dynamic-form/hook/useWatchPagination";
import { DynamicForm } from "@/components/dynamic-form";
import { getInitialValues } from "@/components/dynamic-form/helper/getInitialValues";
import { useSearchConfig } from "@/pages/consumption/view/billing-details/view/desktop-volume-detail/hooks/useSearchConfig";
import { Table } from "@renderbus/ui";
import { useTableColumns } from "@/pages/consumption/view/billing-details/view/desktop-volume-detail/hooks/useTableColumns";
import { useCustomColumns } from "project-config/hooks/useCustomColumns";
import { useFilterColumns } from "project-config/hooks/useFilterColumns";
import {
  OperationArea,
  SearchAreaWrapper,
} from "@/pages/consumption/components/atom";
import { useTranslation } from "react-i18next";
import { useConsumeStore } from "@/pages/consumption/store/ConsumeStore";
import { useExportBill } from "@/pages/consumption/hooks/useExportBill";
import { Modal } from "@/components/renderbus-modal";
import { ExportBillForm } from "@/pages/consumption/components/export-form/export-bill-form";
import { useAccountList } from "@/pages/consumption/view/billing-details/view/desktop-volume-detail/hooks/useAccountList";
import { getDefaultExportName } from "@/pages/consumption/helper/getDefaultExportName";
import { useEffect } from "react";
import styled, { createGlobalStyle } from "styled-components";
import {
  BillDetailsTabsKey,
  BillTabTypeEnum,
} from "@/pages/consumption/constants";
import { Button } from "@renderbus/ui";

export function DesktopVolumeDetail() {
  const {
    form,
    getSearchParams,
    tableData,
    fetchData,
    pageNum,
    setPageNum,
    total,
    pageSize,
  } = useDynamicForm({
    api: "/api/rendering/user/cloudProduction/getUserCloudProductionBillDetails",
    defaultPageSize: 10,
    formatter: {
      time: enhanceEndTime,
      taskIds: (v) => (v ? v.split(",") : undefined),
    },
  });

  const { fetchBillConsume } = useConsumeStore();

  const { data: accountList } = useAccountList();

  const fetch = async (isSearch = false) => {
    if (isSearch) {
      setPageNum(1);
    }
    fetchBillConsume(getSearchParams()).then();
    fetchData().then();
  };

  useWatchPagination(pageSize, pageNum, fetch);

  const { searchConfig } = useSearchConfig();

  const { columns } = useTableColumns();

  const { t } = useTranslation("center");
  const { t: consumptionT } = useTranslation("consumption");

  const defaultUnchecked = [
    "layerName",
    "renderCamera",
    "framesRange",
    "averageDuration",
    "graphicsCardsNum",
    "taskRam",
    "model",
    "organizationStructure",
  ];
  const { checkedValue } = useCustomColumns(columns, "consumption_task_tab", {
    defaultUnchecked,
  });

  const { columns: newColumns } = useFilterColumns(columns, checkedValue);

  const searchParams = getSearchParams({ hiddenPageSize: true });
  const {
    exportForm,
    openModal,
    setModal,
    onOkTask,
    exportFrameForm,
    openFrameModal,
    setFrameModal,
    onFrameOk,
  } = useExportBill({
    api: "/api/rendering/task/export/exportUserCloudProductionBillDetails",
    searchParams,
  });

  const { activeKey, billDetailsActiveKey } = useConsumeStore();

  useEffect(() => {
    if (
      activeKey === BillTabTypeEnum.BillingDetails &&
      (billDetailsActiveKey === BillDetailsTabsKey.VolumeDetail ||
        billDetailsActiveKey === BillDetailsTabsKey.DesktopVolumeDetail)
    ) {
      fetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey, billDetailsActiveKey]);

  return (
    <SubTabWrapper>
      <SearchAreaWrapper>
        <DynamicForm
          initialValues={getInitialValues(searchConfig)}
          searchConfig={searchConfig}
          searchForm={form}
          onSearch={() => fetch(true)}
        />
        <OperationArea>
          <TextButton type="text" onClick={() => setModal(true)}>
            {t("consumption_export")}
          </TextButton>
        </OperationArea>
      </SearchAreaWrapper>
      <Table
        paginationCurrent={pageNum}
        columns={[...newColumns]}
        dataSource={tableData}
        gap={false}
        total={total}
        defaultPageSize={pageSize}
        onPageNumChange={async (num: number) => {
          setPageNum(num);
        }}
      />
      <MoreButtonDropdownStyle />
      <Modal
        className={`renderbus-ui--modal`}
        title={consumptionT("consumption_exprot_task_bill")}
        open={openModal}
        destroyOnClose
        onOk={() => {
          onOkTask();
        }}
        onCancel={() => {
          setModal(false);
        }}
      >
        <ExportBillForm
          form={exportForm}
          defaultExportName={getDefaultExportName(
            searchParams,
            accountList ?? [],
            "task"
          )}
        />
      </Modal>

      <Modal
        className={`renderbus-ui--modal`}
        title={consumptionT("consumption_exprot_frame_bill")}
        open={openFrameModal}
        destroyOnClose
        onOk={() => {
          onFrameOk();
        }}
        onCancel={() => {
          setFrameModal(false);
        }}
      >
        <ExportBillForm
          form={exportFrameForm}
          defaultExportName={getDefaultExportName(
            searchParams,
            accountList ?? [],
            "frame"
          )}
        />
      </Modal>
    </SubTabWrapper>
  );
}

const MoreButtonDropdownStyle = createGlobalStyle`
  .export-child-more-dropdown {
    .ant-dropdown-menu {
      background: transparent;
      border-radius: 4px;
      padding: unset !important;
    }
    .ant-dropdown-menu-item {
      padding: unset !important;
      &:hover {
        background: red !important;
        color: green !important;
      }
    }
  }
`;

const TextButton = styled(Button)`
  width: min-content;
  padding: 0;
  margin-right: 16px;
  color: var(--main-color) !important;
  border: none;
  display: flex;
  align-items: center;
  &:hover {
    color: #13cb75 !important;
  }
`;

const SubTabWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
